// src/pages/Home/ConditionsUtilisationPage.jsx
// ou components/ConditionsUtilisationPage.jsx selon ton emplacement

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaArrowLeft,
  FaBalanceScale,
  FaCheckCircle,
  FaClipboardList,
  FaExclamationTriangle,
  FaFileContract,
  FaGlobe,
  FaPrint,
  FaShieldAlt,
  FaUserShield,
} from "react-icons/fa";

import useScrollToTop from "../../hooks/useScollToTop";
import { getSettings } from "../../services/settingsServices";

function cn(...classes) {
  return classes.filter(Boolean).join(" ");
}

const DEFAULT_SETTINGS = {
  siteweb_club: "",
  nom_club: "",
  adresse_club: "",
  responsable_club: "",
  mail_club: "",
  siret_club: "",
};

const safeValue = (value, fallback = "Non renseigné") => {
  const clean = String(value || "").trim();
  return clean || fallback;
};

const InfoCard = ({ icon: Icon, label, value, href }) => {
  if (!value) return null;

  const content = (
    <div className="group flex h-full items-start gap-4 rounded-3xl border border-slate-200 bg-white/85 p-5 shadow-sm backdrop-blur-xl transition hover:-translate-y-1 hover:border-primary/30 hover:shadow-lg dark:border-white/10 dark:bg-white/5">
      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-primary dark:bg-primary/20">
        <Icon />
      </div>

      <div className="min-w-0">
        <p className="text-xs font-bold uppercase tracking-[0.18em] text-slate-400">
          {label}
        </p>

        <p className="mt-2 break-words text-sm font-bold leading-6 text-slate-800 dark:text-slate-100">
          {value}
        </p>
      </div>
    </div>
  );

  if (!href) return content;

  const isExternal = /^https?:\/\//i.test(href);

  return (
    <a
      href={href}
      target={isExternal ? "_blank" : undefined}
      rel={isExternal ? "noopener noreferrer" : undefined}
      className="block h-full"
    >
      {content}
    </a>
  );
};

const LegalSection = ({ number, title, children }) => {
  return (
    <section className="rounded-[1.7rem] border border-slate-200 bg-white/80 p-5 shadow-sm backdrop-blur-xl dark:border-white/10 dark:bg-white/5 sm:p-6">
      <div className="flex items-start gap-4">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-primary text-sm font-extrabold text-white shadow-lg">
          {number}
        </div>

        <div className="min-w-0">
          <h2 className="font-barlow text-xl font-extrabold leading-tight text-slate-950 dark:text-white sm:text-2xl">
            {title}
          </h2>

          <div className="mt-4 space-y-4 text-sm leading-7 text-slate-600 dark:text-slate-300 sm:text-base sm:leading-8">
            {children}
          </div>
        </div>
      </div>
    </section>
  );
};

const ConditionsSkeleton = () => {
  return (
    <main className="min-h-screen bg-slate-50 pt-[calc(var(--site-navbar-h)+48px)] dark:bg-slate-950">
      <div className="mx-auto max-w-6xl px-4 pb-16 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-3xl text-center">
          <div className="mx-auto h-9 w-52 animate-pulse rounded-full bg-slate-200 dark:bg-slate-800" />
          <div className="mx-auto mt-6 h-12 w-2/3 animate-pulse rounded-2xl bg-slate-200 dark:bg-slate-800" />
          <div className="mx-auto mt-5 h-5 w-3/4 animate-pulse rounded-full bg-slate-200 dark:bg-slate-800" />
        </div>

        <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {Array.from({ length: 4 }).map((_, index) => (
            <div
              key={index}
              className="h-32 animate-pulse rounded-3xl bg-slate-200 dark:bg-slate-800"
            />
          ))}
        </div>

        <div className="mt-10 space-y-5">
          {Array.from({ length: 5 }).map((_, index) => (
            <div
              key={index}
              className="h-36 animate-pulse rounded-[1.7rem] bg-slate-200 dark:bg-slate-800"
            />
          ))}
        </div>
      </div>
    </main>
  );
};

const ConditionsUtilisationPage = () => {
  useScrollToTop();

  const navigate = useNavigate();

  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    let mounted = true;

    const fetchSettings = async () => {
      try {
        setLoading(true);
        setErrorMsg("");

        const data = await getSettings();
        const clubSettings = Array.isArray(data) ? data[0] : data;

        if (!mounted) return;

        setSettings({
          ...DEFAULT_SETTINGS,
          ...(clubSettings || {}),
        });
      } catch (error) {
        console.error(
          "Erreur lors de la récupération des conditions d’utilisation :",
          error
        );

        if (mounted) {
          setErrorMsg("Impossible de charger les informations du club.");
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    fetchSettings();

    return () => {
      mounted = false;
    };
  }, []);

  const siteName = safeValue(settings.siteweb_club, "site internet du club");
  const clubName = safeValue(settings.nom_club, "Le club");
  const publicationManager = safeValue(settings.responsable_club);
  const clubEmail = safeValue(settings.mail_club);

  const siteHref = useMemo(() => {
    const site = String(settings.siteweb_club || "").trim();

    if (!site) return "";

    if (/^https?:\/\//i.test(site)) return site;

    return `https://${site}`;
  }, [settings.siteweb_club]);

  const emailHref = settings.mail_club ? `mailto:${settings.mail_club}` : "";

  const handlePrint = () => {
    window.print();
  };

  if (loading) return <ConditionsSkeleton />;

  return (
    <main className="relative min-h-screen overflow-hidden bg-slate-50 pt-[calc(var(--site-navbar-h)+48px)] text-slate-950 dark:bg-slate-950 dark:text-white">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(242,113,49,0.13),transparent_34%),radial-gradient(circle_at_bottom_right,rgba(59,130,246,0.10),transparent_32%)]" />

      <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(148,163,184,0.14)_1px,transparent_1px),linear-gradient(to_bottom,rgba(148,163,184,0.14)_1px,transparent_1px)] bg-[size:42px_42px] opacity-40 dark:opacity-20" />

      <style>{`
        @media print {
          nav,
          footer,
          .no-print {
            display: none !important;
          }

          main {
            padding-top: 0 !important;
            background: #ffffff !important;
          }

          .print-card {
            box-shadow: none !important;
            border: none !important;
          }
        }
      `}</style>

      <div className="relative mx-auto max-w-6xl px-4 pb-16 sm:px-6 lg:px-8">
        <div className="no-print mb-8 flex flex-wrap items-center justify-between gap-3">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white/80 px-4 py-2.5 text-sm font-bold text-slate-700 shadow-sm backdrop-blur transition hover:-translate-y-0.5 hover:border-primary/30 hover:text-primary dark:border-white/10 dark:bg-white/5 dark:text-slate-200"
          >
            <FaArrowLeft className="text-xs" />
            Retour
          </button>

          <button
            type="button"
            onClick={handlePrint}
            className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-bold text-white shadow-lg transition hover:-translate-y-0.5 hover:brightness-110"
          >
            <FaPrint className="text-sm" />
            Imprimer
          </button>
        </div>

        <section className="mx-auto max-w-3xl text-center">
          <div className="inline-flex items-center gap-2 rounded-full border border-orange-200 bg-orange-50 px-4 py-2 text-xs font-bold uppercase tracking-[0.22em] text-orange-700 shadow-sm dark:border-orange-500/20 dark:bg-orange-500/10 dark:text-orange-300">
            <FaFileContract />
            Textes réglementaires
          </div>

          <h1 className="mt-6 font-barlow text-4xl font-bold tracking-tight text-slate-950 dark:text-white sm:text-5xl lg:text-6xl">
            Conditions{" "}
            <span className="text-primary">d’utilisation</span>
          </h1>

          <p className="mx-auto mt-5 max-w-2xl text-base leading-8 text-slate-600 dark:text-slate-300">
            Ces conditions définissent les règles d’accès et d’utilisation du
            site internet du club, ainsi que les droits et obligations des
            visiteurs.
          </p>

          <p className="mt-4 text-sm font-bold uppercase tracking-[0.18em] text-slate-400">
            {clubName}
          </p>
        </section>

        {errorMsg && (
          <div className="mt-10 rounded-[1.5rem] border border-red-200 bg-red-50 p-4 text-center text-sm font-semibold text-red-700 dark:border-red-500/20 dark:bg-red-500/10 dark:text-red-300">
            {errorMsg}
          </div>
        )}

        <section className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <InfoCard icon={FaGlobe} label="Site concerné" value={siteName} href={siteHref} />

          <InfoCard icon={FaShieldAlt} label="Structure" value={clubName} />

          <InfoCard
            icon={FaUserShield}
            label="Responsable"
            value={publicationManager}
          />

          <InfoCard
            icon={FaBalanceScale}
            label="Contact"
            value={clubEmail}
            href={emailHref}
          />
        </section>

        <article className="print-card mt-10 overflow-hidden rounded-[2.5rem] border border-slate-200 bg-white/90 p-5 shadow-2xl backdrop-blur-xl dark:border-white/10 dark:bg-slate-900/90 sm:p-7 lg:p-8">
          <div className="mb-8 rounded-[2rem] border border-slate-200 bg-slate-50 p-5 dark:border-white/10 dark:bg-white/5">
            <div className="flex items-start gap-4">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                <FaClipboardList />
              </div>

              <div>
                <p className="text-xs font-bold uppercase tracking-[0.18em] text-slate-400">
                  Cadre d’utilisation
                </p>

                <p className="mt-2 text-sm leading-7 text-slate-600 dark:text-slate-300">
                  L’utilisation du site <strong>{siteName}</strong> implique
                  l’acceptation des présentes conditions d’utilisation. Ces
                  dispositions peuvent être mises à jour afin de tenir compte de
                  l’évolution du site ou des services proposés.
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-5">
            <LegalSection number="1" title="Introduction">
              <p>
                Les présentes conditions d’utilisation régissent l’accès et
                l’utilisation du site <strong>{siteName}</strong>. Elles
                s’appliquent à tous les visiteurs, utilisateurs et autres
                personnes qui accèdent ou utilisent le service.
              </p>
            </LegalSection>

            <LegalSection number="2" title="Acceptation des conditions">
              <p>
                En accédant ou en utilisant le site{" "}
                <strong>{siteName}</strong>, vous acceptez d’être lié par ces
                conditions. Si vous n’êtes pas d’accord avec une partie
                quelconque des conditions, vous n’êtes pas autorisé à accéder au
                service.
              </p>
            </LegalSection>

            <LegalSection number="3" title="Modifications des conditions">
              <p>
                <strong>{clubName}</strong> se réserve le droit de modifier ou
                remplacer ces conditions à tout moment. En cas de modification
                importante, le club s’efforcera de fournir une information
                préalable raisonnable avant l’entrée en vigueur des nouvelles
                conditions.
              </p>
            </LegalSection>

            <LegalSection number="4" title="Contenu du site">
              <p>
                Les droits sur le contenu, les images, les matériaux, le design
                et les services fournis ou accessibles via{" "}
                <strong>{siteName}</strong> appartiennent à{" "}
                <strong>{clubName}</strong> ou à ses concédants et sont protégés
                par les lois relatives à la propriété intellectuelle.
              </p>

              <p>
                Vous acceptez de ne pas utiliser le contenu du site d’une manière
                qui violerait ces lois ou les présentes conditions.
              </p>
            </LegalSection>

            <LegalSection number="5" title="Comportements interdits">
              <p>
                Vous acceptez de ne pas utiliser le site{" "}
                <strong>{siteName}</strong> pour :
              </p>

              <ul className="list-disc space-y-2 pl-5">
                <li>
                  Publier du contenu illégal, nuisible, menaçant, abusif,
                  harcelant, diffamatoire, vulgaire, obscène ou répréhensible.
                </li>
                <li>
                  Tenter de sonder, scanner ou tester la vulnérabilité d’un
                  système ou réseau, ou de violer des mesures de sécurité ou
                  d’authentification.
                </li>
                <li>
                  Interférer avec le service, notamment par l’envoi de virus, de
                  surcharge, de spam, d’inondation ou toute autre tentative de
                  perturbation du site.
                </li>
              </ul>
            </LegalSection>

            <LegalSection number="6" title="Limitation de responsabilité">
              <p>
                <strong>{clubName}</strong> ne pourra être tenu responsable des
                dommages de toute nature découlant de l’utilisation ou de
                l’impossibilité d’utiliser le site <strong>{siteName}</strong>,
                même si le club a été informé de la possibilité de tels dommages.
              </p>
            </LegalSection>

            <LegalSection number="7" title="Droit applicable et juridiction">
              <p>
                Les présentes conditions sont régies et interprétées conformément
                aux lois françaises. L’incapacité du club à appliquer un droit ou
                une disposition des présentes conditions ne saurait être
                considérée comme une renonciation à ces droits.
              </p>
            </LegalSection>

            <LegalSection number="8" title="Contact">
              <p>
                Pour toute question relative aux présentes conditions
                d’utilisation, l’utilisateur peut contacter le club à l’adresse
                suivante : <strong>{clubEmail}</strong>.
              </p>
            </LegalSection>
          </div>

          <div
            aria-hidden
            className="mt-8 h-1.5 w-full rounded-full bg-gradient-to-r from-transparent via-primary to-transparent opacity-90"
          />
        </article>

        <div className="no-print mt-8 rounded-[1.5rem] border border-amber-200 bg-amber-50 p-4 text-sm leading-7 text-amber-800 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-200">
          <div className="flex items-start gap-3">
            <FaExclamationTriangle className="mt-1 shrink-0" />
            <p>
              Ce modèle fournit une base de présentation. Pour une publication
              officielle, il est recommandé de vérifier le contenu selon les
              règles applicables à votre structure.
            </p>
          </div>
        </div>
      </div>
    </main>
  );
};

export default ConditionsUtilisationPage;